import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import config from '../config';
import GoogleIdentityButton from './auth/GoogleIdentityButton';
import StepUpDialog from './auth/StepUpDialog';
import { isValidEmail } from '../utils/emailValidation';
import './auth/LoginDesign.css';

const INTERNAL_REDIRECT_PREFIXES = ['/app', '/login', '/register', '/forgot-password'];
// RETIRED 2026-10-04 (owner ruling; Core #404): '/Service-Login' (the central
// browser grant page, Mantis #1284 / M001-D021 SS10.2) is removed from this
// allowlist together with its page. It was previously allowlisted so an
// anonymous visitor bounced through /login could reach the consent card; with
// the page gone that path is no longer a valid redirect target.
//
// M004-F0656 / T-CONN-0249 (owner ruling 2026-10-04): this allowlist used to
// have a second branch that trusted an absolute, cross-origin redirect target
// so a node's own browser-redirect OIDC console login (full detail, and the
// exact retired symbol/client-id names a CI guard now watches for, is in
// docs/design/M004-F0656-Remove-Node-OIDC-Login.md) could full-document-
// navigate the browser back out to Core after a normal WebUI login. That
// method is removed completely, no shim and no flag: node setup/login goes
// through /Service-Link (device-code approval; the central /Service-Login
// grant was retired 2026-10-04) below, routed through the SAME-ORIGIN
// internal-path allowlist immediately below, never a trusted *external*
// redirect target.
const getBrowserOrigin = () => {
  if (typeof window === 'undefined') {
    return 'http://localhost';
  }
  return window.location.origin;
};

const INTERNAL_REDIRECT_EXACT_PATHS = ['/Service-Link', '/Ledger'];

const getInternalRedirectPath = (value) => {
  if (!value) return '';

  try {
    const url = new URL(value, getBrowserOrigin());
    if (url.origin !== getBrowserOrigin()) {
      return '';
    }
    const isExactPath = INTERNAL_REDIRECT_EXACT_PATHS.includes(url.pathname);
    const isAllowedPrefix = INTERNAL_REDIRECT_PREFIXES.some(
      (prefix) => url.pathname === prefix || url.pathname.startsWith(`${prefix}/`),
    );
    if (!isExactPath && !isAllowedPrefix) {
      return '';
    }
    return `${url.pathname}${url.search}${url.hash}`;
  } catch (e) {
    return '';
  }
};

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);
  const [magicLinkStatus, setMagicLinkStatus] = useState('');
  const [magicLinkError, setMagicLinkError] = useState('');
  const [verifyingMagicLink, setVerifyingMagicLink] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState('');
  // M001-D002 §12.5.6 / BUG-074: unlinked OAuth identity branch state.
  // When the backend rejects Google sign-in with LOGIN_METHOD_NOT_LINKED,
  // we surface a dedicated two-CTA UX instead of the generic error copy.
  const [oauthUnlinked, setOauthUnlinked] = useState(null);

  const {
    login,
    loginWithGoogle,
    requestMagicLink,
    verifyMagicLinkToken,
    isAuthenticated,
    user,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const magicLinkToken = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('token') || params.get('magicLinkToken') || params.get('magic_link_token') || '';
  }, [location.search]);

  const rawMagicLinkRedirectTarget = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('redirectTo') || params.get('redirect_to') || location.state?.from?.pathname || '';
  }, [location.search, location.state]);

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const isStepUp = params.get('stepUp') === 'true';
  const encodedChallenge = params.get('challenge');

  const challenge = useMemo(() => {
    if (!encodedChallenge) return null;
    try {
      return JSON.parse(atob(encodedChallenge));
    } catch (e) {
      console.error('Failed to parse stepUp challenge', e);
      return null;
    }
  }, [encodedChallenge]);

  // M004-F0656: every redirect target is now an internal, same-origin router
  // path -- the node-OIDC "document" mode (a full-document navigation to a
  // node's own trusted /oauth/authorize redirect) was the ONLY caller of
  // anything but 'router' mode, and it is removed (see the note above
  // INTERNAL_REDIRECT_EXACT_PATHS).
  const getRedirectTarget = useCallback((role) => {
    const params = new URLSearchParams(location.search);
    const queryRedirect = params.get('redirectTo') || params.get('redirect_to');
    const internalQueryRedirect = getInternalRedirectPath(queryRedirect);
    if (internalQueryRedirect) {
      return internalQueryRedirect;
    }

    const from = location.state?.from?.pathname;
    const internalFrom = getInternalRedirectPath(from);
    if (internalFrom) {
      return internalFrom;
    }

    switch (role) {
      case 'admin':
      case 'super_admin':
        return '/app/event-simple';
      case 'teacher':
        return '/app/teacher/schedule';
      default:
        return '/app/profile';
    }
  }, [location.search, location.state]);

  const navigateAfterAuth = useCallback((target) => {
    navigate(target, { replace: true });
  }, [navigate]);

  const getMagicLinkRequestRedirect = useCallback(() => {
    const params = new URLSearchParams(location.search);
    const queryRedirect = params.get('redirectTo') || params.get('redirect_to');
    return getInternalRedirectPath(queryRedirect)
      || getInternalRedirectPath(location.state?.from?.pathname)
      || '/login';
  }, [location.search, location.state]);

  // Redirect if already authenticated
  React.useEffect(() => {
    if (isAuthenticated && user) {
      if (isStepUp && challenge) {
        return;
      }
      navigateAfterAuth(getRedirectTarget(user.role));
    }
  }, [getRedirectTarget, isAuthenticated, navigateAfterAuth, user, isStepUp, challenge]);

  const handleStepUpComplete = () => {
    navigateAfterAuth(getRedirectTarget(user?.role || 'user'));
  };

  const handleStepUpCancel = () => {
    navigate('/', { replace: true });
  };

  React.useEffect(() => {
    if (!magicLinkToken || isAuthenticated) {
      return;
    }

    let cancelled = false;

    const completeMagicLinkSignIn = async () => {
      setVerifyingMagicLink(true);
      setError('');
      setMagicLinkError('');
      setMagicLinkStatus('Completing your magic-link sign-in…');

      try {
        const userData = await verifyMagicLinkToken(magicLinkToken);
        if (cancelled) {
          return;
        }

        const magicLinkRedirectTarget = userData.redirectTo || rawMagicLinkRedirectTarget;
        const internalRedirect = getInternalRedirectPath(magicLinkRedirectTarget);
        navigateAfterAuth(internalRedirect || getRedirectTarget(userData.role));
      } catch (err) {
        if (!cancelled) {
          setMagicLinkError(err.message || 'Magic-link verification failed.');
          setMagicLinkStatus('');
        }
      } finally {
        if (!cancelled) {
          setVerifyingMagicLink(false);
        }
      }
    };

    completeMagicLinkSignIn();

    return () => {
      cancelled = true;
    };
  }, [getRedirectTarget, isAuthenticated, navigateAfterAuth, rawMagicLinkRedirectTarget, magicLinkToken, verifyMagicLinkToken]);

  const validateForm = () => {
    if (!email) {
      setError('Email is required');
      return false;
    }
    if (!isValidEmail(email)) {
      setError('Please enter a valid email address');
      return false;
    }
    if (!password) {
      setError('Password is required');
      return false;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMagicLinkError('');
    setMagicLinkStatus('');
    setApprovalStatus('');

    if (!validateForm()) return;

    setLoading(true);
    try {
      const userData = await login(email, password);
      navigateAfterAuth(getRedirectTarget(userData.role));
    } catch (err) {
      setError(err.message || 'Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleCredential = async (response) => {
    setError('');
    setMagicLinkError('');
    setMagicLinkStatus('');
    setApprovalStatus('');
    setOauthUnlinked(null);
    setLoading(true);

    try {
      const userData = await loginWithGoogle({
        idToken: response.credential,
      });
      if (userData?.pendingApproval) {
        setApprovalStatus(userData.message || 'Your account is waiting for admin approval. We will email you when access is granted.');
        return;
      }
      navigateAfterAuth(getRedirectTarget(userData.role));
    } catch (err) {
      // M001-D002 §12.5.6 / BUG-074: valid Google identity, no linked
      // StudioAEye account. Branch to a dedicated UX with two CTAs
      // (create account vs. sign in and link) instead of the generic
      // "Google sign-in failed." copy.
      if (err && err.code === 'LOGIN_METHOD_NOT_LINKED') {
        setOauthUnlinked({
          provider: err.provider || 'google',
          providerEmail: err.providerEmail || '',
          emailAccountExists: Boolean(err.emailAccountExists),
        });
        if (err.providerEmail) {
          setEmail(err.providerEmail);
        }
        setError('');
      } else if (err?.pendingApproval) {
        setApprovalStatus(err.message || 'Your account is waiting for admin approval. We will email you when access is granted.');
      } else {
        setError(err.message || 'Google sign-in failed.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUnlinkedCreateAccount = () => {
    if (!oauthUnlinked) return;
    navigate('/register', {
      state: {
        verifiedEmail: oauthUnlinked.providerEmail || '',
        fromOauthUnlinkedLogin: true,
        oauthProvider: oauthUnlinked.provider,
      },
    });
  };

  const dismissUnlinkedBranch = () => {
    setOauthUnlinked(null);
  };

  const handleMagicLinkRequest = async () => {
    setError('');
    setMagicLinkError('');
    setMagicLinkStatus('');
    setApprovalStatus('');

    if (!email || !isValidEmail(email)) {
      setMagicLinkError('Enter a valid email address to receive a magic link.');
      return;
    }

    setMagicLinkLoading(true);

    try {
      const result = await requestMagicLink(email, getMagicLinkRequestRedirect());
      setMagicLinkStatus(result.message || 'If the request could be accepted, a sign-in link will arrive shortly.');
    } catch (err) {
      setMagicLinkError(err.message || 'Magic-link request failed.');
    } finally {
      setMagicLinkLoading(false);
    }
  };

  const authBusy = loading || verifyingMagicLink;
  const hasGoogleSignIn = Boolean(config.oauth.googleClientId)
    || (typeof window !== 'undefined' && Boolean(window.__STUDIOAEYE_TEST_GOOGLE_CLIENT_ID || window.google?.accounts?.id));
  const combinedStatus = magicLinkStatus || approvalStatus;
  const combinedError = error || magicLinkError;

  return (
    <div className="login-page">
      {isStepUp && challenge && (
        <StepUpDialog
          challenge={challenge}
          onComplete={handleStepUpComplete}
          onCancel={handleStepUpCancel}
        />
      )}
      <header className="login-header">
        <div className="login-header-inner">
          <Link to="/" className="login-logo">
            Studio<span className="login-logo-accent">AEye</span>
          </Link>
          <a
            href="http://dancebelfast.com"
            className="login-back-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            ← Public schedule
          </a>
        </div>
      </header>

      <main className="login-main">
        <div className="login-card">
          <div className="login-heading">
            <h1 className="login-title">Welcome back</h1>
            <p className="login-subtitle">
              New to StudioAEye?{' '}
              <Link to="/register" state={email ? { prefilledEmail: email } : undefined} className="login-link">
                Sign up
              </Link>
            </p>
          </div>

          <form onSubmit={handleSubmit} className="login-form" noValidate>
            <div className="login-field">
              <label htmlFor="email" className="login-label">
                Your email address
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="login-input"
                disabled={authBusy}
                aria-describedby={combinedError ? 'login-error' : undefined}
              />
            </div>

            <div className="login-field">
              <label htmlFor="password" className="login-label">
                Your password
              </label>
              <div className="login-password-wrapper">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="login-input"
                  disabled={authBusy}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="login-password-toggle"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            {combinedError && (
              <div id="login-error" role="alert" className="login-alert login-alert-error">
                {combinedError}
              </div>
            )}

            {combinedStatus && (
              <div
                className="login-alert login-alert-success"
                data-testid={approvalStatus ? 'oauth-pending-approval-message' : undefined}
              >
                {combinedStatus}
              </div>
            )}

            <button
              type="submit"
              disabled={authBusy}
              className="login-submit"
            >
              {loading ? (
                <>
                  <span className="login-spinner" />
                  Logging in…
                </>
              ) : (
                'Log in'
              )}
            </button>

            <div className="login-forgot">
              <Link to={`/forgot-password${email ? `?email=${encodeURIComponent(email)}` : ''}`}>
                Trouble logging in?
              </Link>
            </div>
          </form>

          <div className="login-divider">
            <div className="login-divider-line" />
            <span className="login-divider-text">Or log in with</span>
            <div className="login-divider-line" />
          </div>

          {oauthUnlinked && (
            <div
              className="login-alert login-alert-error login-oauth-unlinked"
              role="alert"
              data-testid="oauth-unlinked-branch"
              data-provider={oauthUnlinked.provider}
              data-email-account-exists={oauthUnlinked.emailAccountExists ? 'true' : 'false'}
            >
              <p className="login-oauth-unlinked-title">
                This Google account isn&apos;t linked to a StudioAEye account yet.
              </p>
              {oauthUnlinked.providerEmail && (
                <p className="login-oauth-unlinked-email">
                  Google signed you in as <strong>{oauthUnlinked.providerEmail}</strong>.
                </p>
              )}
              {oauthUnlinked.emailAccountExists ? (
                <>
                  <p>
                    A StudioAEye account already uses this email. Sign in with
                    your existing method, then link Google from your account
                    settings.
                  </p>
                  <div className="login-oauth-unlinked-actions">
                    <button
                      type="button"
                      className="login-submit"
                      onClick={dismissUnlinkedBranch}
                      data-testid="oauth-unlinked-cta-sign-in"
                    >
                      Sign in with your existing method
                    </button>
                    <button
                      type="button"
                      className="login-provider-btn"
                      onClick={handleUnlinkedCreateAccount}
                      data-testid="oauth-unlinked-cta-create-account"
                    >
                      Create a separate account
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p>
                    No StudioAEye account matches this email. Create one to
                    continue with Google.
                  </p>
                  <div className="login-oauth-unlinked-actions">
                    <button
                      type="button"
                      className="login-submit"
                      onClick={handleUnlinkedCreateAccount}
                      data-testid="oauth-unlinked-cta-create-account"
                    >
                      Create an account
                    </button>
                    <button
                      type="button"
                      className="login-provider-btn"
                      onClick={dismissUnlinkedBranch}
                      data-testid="oauth-unlinked-cta-use-different"
                    >
                      Use a different Google account
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          <div className="login-providers">
            {hasGoogleSignIn ? (
              <div className="login-google-container">
                <GoogleIdentityButton
                  onCredential={handleGoogleCredential}
                  disabled={authBusy}
                  width={400}
                  shape="pill"
                  text="continue_with"
                  theme="outline"
                  enableOneTap
                  fallbackLabel="Google sign-in unavailable"
                />
              </div>
            ) : (
              <div className="login-google-fallback">Google sign-in unavailable</div>
            )}

            {/* Apple Sign-In is intentionally hidden from the web login page
                until Apple Developer Program provisioning is in place.
                See BUG-050 and M001-D002 §12.3.2. The handler and state are
                retained so the button can be re-enabled by restoring the JSX
                once real credentials exist. */}

            <button
              type="button"
              onClick={handleMagicLinkRequest}
              disabled={magicLinkLoading || authBusy}
              className="login-provider-btn"
            >
              {magicLinkLoading ? (
                <>
                  <span className="login-spinner login-spinner-muted" />
                  Sending magic link…
                </>
              ) : (
                <>
                  <svg
                    className="login-provider-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                  Email me a magic link
                </>
              )}
            </button>
          </div>

          <p className="login-footer">© StudioAEye {new Date().getFullYear()}</p>
        </div>
      </main>
    </div>
  );
};

export default Login;
